// @flow

import { connectRoutes, redirect, NOT_FOUND as LIB_NOT_FOUND } from 'redux-first-router'
import { createBrowserHistory } from 'history'
import { logout, isAdmin, isViewer } from './authentication'
import { getCampaigns } from './campaigns'
import { fetchAllowedCampaigns, setCurrentCampaign } from './allowed-campaigns'
import { DataGridDuck } from 'react-redux-components'
import { resetData as resetReportsData } from './reports'

import type { Dispatch, GetState } from './index'

// Action types
const BASEURL_ROUTE: 'router/BASEURL_ROUTE' = 'router/BASEURL_ROUTE'
export const CAMPAIGNS_ROUTE: 'router/CAMPAIGNS_ROUTE' = 'router/CAMPAIGNS_ROUTE'
const PANEL_ROUTE: 'router/PANEL_ROUTE' = 'router/PANEL_ROUTE'
const PANEL_LIST_ROUTE: 'router/PANEL_LIST_ROUTE' = 'router/PANEL_LIST_ROUTE'
const VISITS_ROUTE: 'router/VISITS_ROUTE' = 'router/VISITS_ROUTE'
const VISITS_LIST_ROUTE: 'router/VISITS_LIST_ROUTE' = 'router/VISITS_LIST_ROUTE'
const REPORTS_ROUTE: 'router/REPORTS_ROUTE' = 'router/REPORTS_ROUTE'
const REPORTS_LIST_ROUTE: 'router/REPORTS_LIST_ROUTE' = 'router/REPORTS_LIST_ROUTE'
const LOGIN_ROUTE: 'router/LOGIN_ROUTE' = 'router/LOGIN_ROUTE'
const LOGOUT_ROUTE: 'router/LOGOUT_ROUTE' = 'router/LOGOUT_ROUTE'
const USERS_ROUTE: 'router/USERS_ROUTE' = 'router/USERS_ROUTE'
export const CHANGE_PASSWORD_ROUTE: 'router/CHANGE_PASSWORD_ROUTE' = 'router/CHANGE_PASSWORD_ROUTE'
const NOT_FOUND: '@@redux-first-router/NOT_FOUND' = LIB_NOT_FOUND

type ACTION_BASEURL_ROUTE = {| type: typeof BASEURL_ROUTE |}
type ACTION_CAMPAIGNS_ROUTE = {| type: typeof CAMPAIGNS_ROUTE |}
type ACTION_PANEL_ROUTE = {| type: typeof PANEL_ROUTE |}
type ACTION_PANEL_LIST_ROUTE = {| type: typeof PANEL_LIST_ROUTE |}
type ACTION_VISITS_ROUTE = {| type: typeof VISITS_ROUTE |}
type ACTION_VISITS_LIST_ROUTE = {| type: typeof VISITS_LIST_ROUTE |}
type ACTION_REPORTS_ROUTE = {| type: typeof REPORTS_ROUTE, payload: { type: string } |}
type ACTION_REPORTS_LIST_ROUTE = {| type: typeof REPORTS_LIST_ROUTE |}
type ACTION_LOGIN_ROUTE = {| type: typeof LOGIN_ROUTE |}
type ACTION_LOGOUT_ROUTE = {| type: typeof LOGOUT_ROUTE |}
type ACTION_USERS_ROUTE = {| type: typeof USERS_ROUTE |}
type ACTION_CHANGE_PASSWORD_ROUTE = {| type: typeof CHANGE_PASSWORD_ROUTE |}
type ACTION_NOT_FOUND = {| type: typeof NOT_FOUND |}

export type RouterAction =
  | ACTION_BASEURL_ROUTE
  | ACTION_CAMPAIGNS_ROUTE
  | ACTION_PANEL_ROUTE
  | ACTION_PANEL_LIST_ROUTE
  | ACTION_VISITS_ROUTE
  | ACTION_VISITS_LIST_ROUTE
  | ACTION_REPORTS_ROUTE
  | ACTION_REPORTS_LIST_ROUTE
  | ACTION_LOGIN_ROUTE
  | ACTION_LOGOUT_ROUTE
  | ACTION_USERS_ROUTE
  | ACTION_CHANGE_PASSWORD_ROUTE
  | ACTION_NOT_FOUND

export type RouterState = {|
  pathname: string,
  type: string,
  payload: Object,
  prev: Object,
  kind: string,
  routesMap: Object,
|}

const history = createBrowserHistory()

const routesMap = {
  [BASEURL_ROUTE]: {
    path: '/',
    thunk: (dispatch: Dispatch, getState: GetState) => {
      dispatch(redirect(goToCampaigns()))
    },
  },
  [LOGIN_ROUTE]: '/login',
  [LOGOUT_ROUTE]: {
    path: '/logout',
    thunk: (dispatch: Dispatch, getState: GetState) => {
      dispatch(logout())
    },
  },
  [CAMPAIGNS_ROUTE]: {
    path: '/campaigns',
    thunk: (dispatch: Dispatch, getState: GetState) => {
      dispatch(getCampaigns())
    },
  },
  [PANEL_ROUTE]: {
    path: '/panels',
    thunk: (dispatch: Dispatch, getState: GetState) => {
      dispatch(setCurrentCampaign(''))
      dispatch(DataGridDuck.creators.resetDatagrid('panelGrid'))
    },
  },
  [PANEL_LIST_ROUTE]: {
    path: '/panels/:campaignName',
    thunk: async (dispatch: Dispatch, getState: GetState) => {
      let { campaignName } = getState().location.payload
      try {
        let campaigns = await fetchAllowedCampaigns(getState)
        if (campaigns.indexOf(campaignName) !== -1) {
          dispatch(setCurrentCampaign(campaignName))
          dispatch(DataGridDuck.creators.resetDatagrid('panelGrid'))
        } else {
          dispatch({ type: NOT_FOUND })
        }
      } catch (err) {
        dispatch({ type: NOT_FOUND })
      }
    },
  },
  [VISITS_ROUTE]: {
    path: '/visits',
    thunk: (dispatch: Dispatch, getState: GetState) => {
      dispatch(setCurrentCampaign(''))
      dispatch(DataGridDuck.creators.resetDatagrid('visitsGrid'))
    },
  },
  [VISITS_LIST_ROUTE]: {
    path: '/visits/:campaignName',
    thunk: async (dispatch: Dispatch, getState: GetState) => {
      let { campaignName } = getState().location.payload
      try {
        let campaigns = await fetchAllowedCampaigns(getState)
        if (campaigns.indexOf(campaignName) !== -1) {
          dispatch(setCurrentCampaign(campaignName))
          dispatch(DataGridDuck.creators.resetDatagrid('visitsGrid'))
        } else {
          dispatch({ type: NOT_FOUND })
        }
      } catch (err) {
        dispatch({ type: NOT_FOUND })
      }
    },
  },
  [REPORTS_ROUTE]: {
    path: '/reports/:type',
    thunk: (dispatch: Dispatch, getState: GetState) => {
      dispatch(setCurrentCampaign(''))
      dispatch(resetReportsData())
    },
  },
  [REPORTS_LIST_ROUTE]: {
    path: '/reports/:type/:campaignName',
    thunk: async (dispatch: Dispatch, getState: GetState) => {
      dispatch(resetReportsData())
      dispatch(setCurrentCampaign(''))
      let { campaignName } = getState().location.payload
      try {
        let campaigns = await fetchAllowedCampaigns(getState)
        if (campaigns.indexOf(campaignName) !== -1) {
          dispatch(setCurrentCampaign(campaignName))
        } else {
          dispatch({ type: NOT_FOUND })
        }
      } catch (err) {
        dispatch({ type: NOT_FOUND })
      }
    },
  },
  [USERS_ROUTE]: {
    path: '/users',
    thunk: (dispatch: Dispatch, getState: GetState) => {
      dispatch(DataGridDuck.creators.resetDatagrid('gridUsers'))
    },
  },
  [CHANGE_PASSWORD_ROUTE]: '/change_password',
}

const options = {
  onBeforeChange: (dispatch: Dispatch, getState: GetState, action: Object) => {
    if (action.action.type === NOT_FOUND) {
      dispatch(redirect(goToCampaigns()))
    }
    if (action.action.type !== CHANGE_PASSWORD_ROUTE) {
      let user = getState().auth.data.credentials.user
      if (user && user.changePasswordNeeded) {
        dispatch(redirect(goToChangePassword()))
      }
    }
    if (action.action.type === USERS_ROUTE) {
      if (!isAdmin(getState())) {
        dispatch(redirect(goToCampaigns()))
      }
    }
    if (
      (action.action.type === REPORTS_ROUTE || action.action.type === REPORTS_LIST_ROUTE) &&
      action.action.payload.type === 'details'
    ) {
      if (isViewer(getState())) {
        dispatch(redirect(goToCampaigns()))
      }
    }
    if (action.action.type === PANEL_ROUTE || action.action.type === PANEL_LIST_ROUTE) {
      if (isViewer(getState())) {
        dispatch(redirect(goToCampaigns()))
      }
    }
    if (action.action.type === VISITS_ROUTE || action.action.type === VISITS_LIST_ROUTE) {
      if (isViewer(getState())) {
        dispatch(redirect(goToCampaigns()))
      }
    }
  },
}

const { reducer, enhancer, middleware } = connectRoutes(history, routesMap, options)

const goBack = () => {
  history.goBack()
}

const historyPush = (path: string) => {
  history.push(path)
}

const goToCampaigns = () => ({ type: CAMPAIGNS_ROUTE })
const goToPanels = () => ({ type: PANEL_ROUTE })
const goToVisits = () => ({ type: VISITS_ROUTE })
const goToReports = (report: string) => ({ type: REPORTS_ROUTE, payload: { type: report } })
const goToLogout = () => ({ type: LOGOUT_ROUTE })
const goToLogin = () => ({ type: LOGOUT_ROUTE })
const goToUsers = () => ({ type: USERS_ROUTE })
const goToChangePassword = () => ({ type: CHANGE_PASSWORD_ROUTE })

export const creators = {
  goBack,
  historyPush,
  goToCampaigns,
  goToPanels,
  goToVisits,
  goToReports,
  goToLogout,
  goToLogin,
  goToUsers,
  goToChangePassword,
}
export {
  reducer,
  enhancer,
  middleware,
  goBack,
  historyPush,
  goToCampaigns,
  goToPanels,
  goToVisits,
  goToReports,
  goToLogout,
  goToLogin,
  goToUsers,
  goToChangePassword,
}

export default creators



// WEBPACK FOOTER //
// ./src/store/router.js